import { call, put, select, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import errorHandler from "common/errorHandler";
import * as actions from "./actions";

// No tenantid header — the tenant is determined server-side from the auth-token claims.

const getUser = (state) => state.userLogin.user;

// The user's goldenstewardship grants, from every role that grants the resource.
export function* getGoldenStewardshipPermissionRequest(api) {
    try {
        const user = yield select(getUser);
        const response = yield call([api, api.get], `${Endpoints.permissions}/api/v1/users/${user.userId}/permissions/resources/goldenstewardship`);
        const permission = { ...actions.NO_GOLDEN_STEWARDSHIP_PERMISSION };
        for (const item of (response && response.data && response.data.Items) || []) {
            // As the services decide: only an ALLOW grant counts.
            if (item.effect !== "ALLOW") {
                continue;
            }
            const granted = item.actions === "*" ? Object.keys(actions.GOLDEN_STEWARDSHIP_ACTIONS) : item.actions || [];
            for (const action of granted) {
                const flag = actions.GOLDEN_STEWARDSHIP_ACTIONS[String(action).toLowerCase()];
                if (flag) {
                    permission[flag] = true;
                }
            }
        }
        yield put(actions.getGoldenStewardshipPermissionSuccess(permission));
    } catch (error) {
        yield put(actions.getGoldenStewardshipPermissionFailure(errorHandler(error, "Get", "golden stewardship permission")));
    }
}

export function* nominateGoldenNominalRequest(api, action) {
    try {
        const { businessAreaId, schemaId, dataSetId } = action.params;
        yield call([api, api.put], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/golden`, {});
        yield put(actions.nominateGoldenNominalSuccess({ schemaId, dataSetId }));
    } catch (error) {
        yield put(actions.nominateGoldenNominalFailure(errorHandler(error, "Nominate", "golden nominal dataset")));
    }
}

export function* denominateGoldenNominalRequest(api, action) {
    try {
        const { businessAreaId, schemaId, dataSetId } = action.params;
        yield call([api, api.delete], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/golden`);
        yield put(actions.denominateGoldenNominalSuccess({ schemaId }));
    } catch (error) {
        yield put(actions.denominateGoldenNominalFailure(errorHandler(error, "De-nominate", "golden nominal dataset")));
    }
}

export function* setPrecedenceTierRequest(api, action) {
    try {
        const { businessAreaId, schemaId, dataSetId, tier } = action.params;
        const result = yield call([api, api.put], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/precedence`, { tier });
        yield put(actions.setPrecedenceTierSuccess(result.data));
    } catch (error) {
        yield put(actions.setPrecedenceTierFailure(errorHandler(error, "Set", "precedence tier")));
    }
}

// PRE-2797 — mark a dataset as a utility source, or clear it.
export function* setDataSetRoleRequest(api, action) {
    try {
        const { businessAreaId, schemaId, dataSetId, role } = action.params;
        const result = yield call([api, api.put], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/role`, { role });
        yield put(actions.setDataSetRoleSuccess(result.data));
    } catch (error) {
        yield put(actions.setDataSetRoleFailure(errorHandler(error, "Set", "dataset role")));
    }
}

// PRE-2797 — truncate a utility dataset (a job: retires every record).
export function* truncateDataSetRequest(api, action) {
    try {
        const { businessAreaId, schemaId, dataSetId } = action.params;
        const result = yield call([api, api.delete], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/records`);
        yield put(actions.truncateDataSetSuccess(dataSetId, result.data && result.data.jobId));
    } catch (error) {
        yield put(actions.truncateDataSetFailure(errorHandler(error, "Truncate", "dataset")));
    }
}

export default function* ({ api }) {
    yield takeLatest(actions.NOMINATE_GOLDEN_NOMINAL_REQUEST, nominateGoldenNominalRequest, api);
    yield takeLatest(actions.DENOMINATE_GOLDEN_NOMINAL_REQUEST, denominateGoldenNominalRequest, api);
    yield takeLatest(actions.SET_PRECEDENCE_TIER_REQUEST, setPrecedenceTierRequest, api);
    yield takeLatest(actions.SET_DATASET_ROLE_REQUEST, setDataSetRoleRequest, api);
    yield takeLatest(actions.TRUNCATE_DATASET_REQUEST, truncateDataSetRequest, api);
    yield takeLatest(actions.GET_GOLDEN_STEWARDSHIP_PERMISSION_REQUEST, getGoldenStewardshipPermissionRequest, api);
}
